import {useMemo, useState} from 'react';
import {toast} from 'react-toastify';

import {
  type EntityIntegrationsQuery,
  type EntityQuery,
  useCreateGenericWebhookIntegrationMutation,
  useTestGenericWebhookIntegrationMutation,
  useUpdateGenericWebhookIntegrationMutation,
} from '../../generated/graphql';
import {type Entity} from '../../types/graphql';
import {propagateErrorsContext} from '../../util/errors';
import {type GenericWebhook, toGenericWebhookIntegration} from './types';

export function useWebhooksFromEntity(
  entity: Entity | EntityQuery['entity'] | EntityIntegrationsQuery['entity']
) {
  return useMemo(() => {
    const genericWebhooks: GenericWebhook[] = [];
    for (const integrationEdge of entity?.integrations?.edges ?? []) {
      if (integrationEdge?.node) {
        const genericWebhook = toGenericWebhookIntegration(
          integrationEdge?.node
        );
        if (genericWebhook) {
          genericWebhooks.push(genericWebhook);
        }
      }
    }
    return genericWebhooks;
  }, [entity?.integrations?.edges]);
}

export function useCreateGenericWebhook() {
  const [createGenericWebhookMutation] =
    useCreateGenericWebhookIntegrationMutation({
      context: propagateErrorsContext(),
    });
  const [isLoading, setIsLoading] = useState(false);
  async function createGenericWebhook(
    entityName: string,
    url: string,
    webhookName: string,
    accessTokenRef?: string,
    secretRef?: string
  ) {
    let createWebhook;
    setIsLoading(true);
    try {
      if (entityName !== '' && webhookName !== '' && url !== '') {
        createWebhook = await createGenericWebhookMutation({
          variables: {
            entityName,
            webhookName,
            urlEndpoint: url,
            accessTokenRef,
            secretRef,
          },
        });
      }
      toast('Created a new webhook!');
      setIsLoading(false);
      return createWebhook;
    } catch (error) {
      toast('Something went wrong while trying to create the webhook');
      setIsLoading(false);
      return undefined;
    }
  }
  return {createGenericWebhook, isLoading};
}

export function useUpdateGenericWebhook() {
  const [updateGenericWebhookMutation] =
    useUpdateGenericWebhookIntegrationMutation({
      context: propagateErrorsContext(),
    });
  const [isLoading, setIsLoading] = useState(false);
  async function updateGenericWebhook(
    integrationID: string,
    url: string,
    webhookName: string,
    accessTokenRef?: string,
    secretRef?: string
  ) {
    let updateWebhook;
    setIsLoading(true);
    try {
      if (integrationID !== '' && webhookName !== '' && url !== '') {
        updateWebhook = await updateGenericWebhookMutation({
          variables: {
            integrationID,
            webhookName,
            urlEndpoint: url,
            accessTokenRef,
            secretRef,
          },
        });
      }
      toast('Updated webhook!');
      setIsLoading(false);
      return updateWebhook;
    } catch (error) {
      toast('Something went wrong while trying to edit the webhook');
      setIsLoading(false);
      return undefined;
    }
  }
  return {updateGenericWebhook, isLoading};
}

export function useTestGenericWebhook() {
  const [testGenericWebhookMutation] = useTestGenericWebhookIntegrationMutation(
    {
      context: propagateErrorsContext(),
    }
  );
  const [isLoading, setIsLoading] = useState(false);
  async function testGenericWebhook(
    entityName: string,
    url: string,
    requestPayload: string,
    accessTokenRef?: string,
    secretRef?: string
  ) {
    let testWebhook;
    setIsLoading(true);
    try {
      if (entityName !== '' && url !== '') {
        testWebhook = await testGenericWebhookMutation({
          variables: {
            entityName,
            urlEndpoint: url,
            accessTokenRef,
            secretRef,
            requestPayload,
          },
        });
      }
      setIsLoading(false);
      return testWebhook;
    } catch (error) {
      toast('Something went wrong while trying to test the webhook');
      setIsLoading(false);
      return undefined;
    }
  }
  return {testGenericWebhook, isLoading};
}
